/* eslint-disable react-native/no-inline-styles */
import React, { useMemo, useState, Fragment } from "react";
import { ControllerRenderProps, FieldValues } from "react-hook-form";
import { FlatList, StyleSheet, Image } from "react-native";
import { ActivityIndicator, List, Paragraph, Title, useTheme, TextInput } from "react-native-paper";
import { useTranslation } from "react-i18next";
import { nonAccentVietnamese } from "app/shared/util/validate";
import { spacing } from "app/shared/styles/spacing";
import { Block } from "../../blocks/block";
import { testProps } from "app/shared/util/test-props";
import { Constant } from "app/shared/constant/constant";

export interface ItemCompleteProps {
  value: string;
  label: string;
  other?: string;
  icon?: string;
  name?: string;
}

const TYPE_ICON = {
  ICON: "ICON",
  URI: "URI",
};

type AutoCompleteProps = {
  field: ControllerRenderProps<FieldValues, string>;
  title: string;
  options: ItemCompleteProps[];
  onSelect?: Function;
  pending?: boolean;
  showSearch?: boolean;
  typeSearch?: string;
  showIcon?: boolean;
};

function AutoComplete(props: AutoCompleteProps) {
  const { t } = useTranslation();
  const { options, field, pending = false, title = "", showSearch = false, typeSearch, showIcon = true, onSelect = () => {} } = props;
  const [search, setSearch] = useState("");
  const [data, setData] = useState<ItemCompleteProps[]>([]);
  const { colors, fonts, roundness } = useTheme();
  const [loading, setLoading] = React.useState(pending);

  const textInputStyleCommonProps: object = {
    style: {
      color: colors.accent,
      backgroundColor: colors.surface,
      fontWeight: "700",
      textShadowColor: "black",
      marginHorizontal: spacing.huge,
    },
    mode: "outlined",
    outlineColor: colors.divider,
    theme: {
      colors: {
        primary: colors.accent,
        text: colors.accent,
      },
    },
  };
  const scrollRef = React.useRef<FlatList>(null);

  const styles = useMemo(
    () =>
      StyleSheet.create({
        flatList: {
          flex: 1,
          paddingHorizontal: spacing.huge,
          paddingVertical: spacing.medium,
        },
        titleModal: {
          color: colors.accent,
        },
      }),
    [colors.accent],
  );

  const onChangeText = (text: string) => {
    let dataSearch: any;
    if (typeSearch?.length && typeSearch === Constant.TEMPLATE_TYPE.INTERBANK_TRANSFER) {
      dataSearch = options.filter((option) => {
        return (
          nonAccentVietnamese(option.label.toLowerCase()).indexOf(nonAccentVietnamese(text.toLowerCase())) >= 0 ||
          (option.other?.length && nonAccentVietnamese(option.other.toLowerCase()).indexOf(nonAccentVietnamese(text.toLowerCase())) >= 0)
        );
      });
    } else {
      dataSearch = options.filter(
        (option) => nonAccentVietnamese(option.label.toLowerCase()).indexOf(nonAccentVietnamese(text.toLowerCase())) >= 0,
      );
    }
    setData(dataSearch);
    setSearch(text);
  };

  const onPressAction = (item: ItemCompleteProps) => {
    field.onChange(item.value);
    onSelect(item);
  };

  React.useEffect(() => {
    if (options) {
      const dataParams: any = options.filter((option) => option.label.toLowerCase());
      setData(dataParams);
    }
  }, [options]);

  React.useEffect(() => {
    setLoading(pending);
  }, [pending]);

  const FormLoading = (
    <Block minHeight={100} center middle>
      <ActivityIndicator color={colors.primary} size={"large"} />
    </Block>
  );

  const ItemView = ({ item, index }: { item: ItemCompleteProps; index: number }) => {
    if (showIcon) {
      let type = TYPE_ICON.ICON;
      if (item.icon?.length && item.icon.toLowerCase().indexOf("http") >= 0) {
        type = TYPE_ICON.URI;
      }
      let isImage: boolean = false;
      if (
        typeSearch?.length &&
        (typeSearch === Constant.TEMPLATE_TYPE.INTERBANK_TRANSFER || typeSearch === Constant.TEMPLATE_TYPE.INTERBANK_FAST_TRANSFER)
      ) {
        isImage = true;
      }
      return (
        <List.Item
          style={{
            height: spacing.macro,
            borderRadius: roundness,
            justifyContent: "center",
            backgroundColor: `${item.value}` === field.value ? colors.accent : "transparent",
          }}
          key={index}
          title={item.label}
          onPress={() => onPressAction(item)}
          titleStyle={{
            ...fonts.medium,
            paddingHorizontal: spacing.medium,
            color: `${item.value}` === field.value ? colors.whiteText : colors.blackText,
          }}
          left={() =>
            item.icon?.length && isImage ? (
              <Image source={{ uri: item.icon }} style={{ width: spacing.massive, height: spacing.massive, borderRadius: spacing.large }} />
            ) : item.icon?.length ? (
              <List.Icon
                icon={type === TYPE_ICON.URI ? { uri: item.icon } : item.icon}
                style={{ width: spacing.massive, height: spacing.massive }}
                color={`${item.value}` === field.value ? colors.surface : colors.accent}
              />
            ) : (
              <Block w={spacing.macro} />
            )
          }
          {...testProps(index.toString())}
        />
      );
    } else
      return (
        <List.Item
          style={{
            height: spacing.macro,
            borderRadius: roundness,
            justifyContent: "center",
            backgroundColor: `${item.value}` === field.value ? colors.accent : "transparent",
          }}
          key={index}
          title={item.label}
          onPress={() => onPressAction(item)}
          titleStyle={{
            ...fonts.medium,
            paddingHorizontal: spacing.medium,
            color: `${item.value}` === field.value ? colors.whiteText : colors.blackText,
          }}
        />
      );
  };
  const ListEmptyComponent = (
    <Block center middle flex={1} p={spacing.medium}>
      <Paragraph>{t("COMMON_KHONG_CO_DU_LIEU")}</Paragraph>
    </Block>
  );

  const TitleModal = (
    <Fragment>
      <Block ph={spacing.huge} pv={spacing.medium}>
        <Title style={styles.titleModal}>{title}</Title>
      </Block>
    </Fragment>
  );

  return (
    <>
      {TitleModal}
      {showSearch ? (
        <TextInput
          {...textInputStyleCommonProps}
          label={t("COMMON_TIM_KIEM")}
          value={search}
          onChangeText={onChangeText}
          left={<TextInput.Icon name="table-search" size={spacing.huge} />}
        />
      ) : null}
      <FlatList
        ref={scrollRef}
        style={styles.flatList}
        data={data}
        getItemLayout={(_, index) => ({
          length: 48,
          offset: 48 * index,
          index,
        })}
        ListEmptyComponent={!loading ? ListEmptyComponent : null}
        scrollToOverflowEnabled
        onScroll={({ nativeEvent: { contentSize, layoutMeasurement, contentOffset } }) => {
          const distanceFromEnd = contentSize.height - layoutMeasurement.height - contentOffset.y;
          if (distanceFromEnd < 0) return false;
        }}
        renderItem={ItemView}
        initialNumToRender={10}
        keyExtractor={(item, index) => String(index)}
        ListFooterComponent={loading ? FormLoading : null}
      />
    </>
  );
}

export default AutoComplete;
